import { useCallback, useState } from 'react';

import {
  api,
  type ApproveStageData,
  type CollectStageContractor,
  type CollectStageData,
  type CommissionStageData,
  type GpStageData,
  type GpStageRow,
  type PayrollPreview,
  type ReconcileStageData,
  type SubmitStageData,
  type TierInfo,
  type VerifyStageContractor,
  type VerifyStageData,
  type VerifyWorkerDetail,
} from '../api';
import { Cell, ExportButton, Row, SearchBar, Spinner, Table } from '../components/Ui';
import { Modal } from '../components/Modal';
import { useAsyncData } from '../hooks/useAsyncData';
import { useSortedRows } from '../hooks/useSortedRows';
import { FormulaTooltip, ShowFormulasToggle } from '../components/FormulaBar';
import { hours, money, rate, shortDate } from '../format';

export function StagePanel({
  stageId,
  onRefresh,
  periodId,
}: {
  stageId: string;
  stageStatus: 'complete' | 'in_progress' | 'pending';
  nextStageName?: string | undefined;
  onRefresh?: () => void;
  onAdvance?: () => void;
  periodId?: string;
}) {
  const refreshProp = onRefresh !== undefined ? { onRefresh } : {};
  const periodProp = periodId !== undefined ? { periodId } : {};

  const panel = (() => {
    switch (stageId) {
      case 'verify':
        return <VerifyPanel {...refreshProp} {...periodProp} />;
      case 'stage_payroll':
        return <PayrollPanel {...refreshProp} {...periodProp} />;
      case 'approve':
        return <ApprovePanel {...refreshProp} {...periodProp} />;
      case 'submit':
        return <SubmitPanel {...refreshProp} {...periodProp} />;
      case 'reconcile':
        return <ReconcilePanel {...refreshProp} {...periodProp} />;
      default:
        return null;
    }
  })();

  return <>{panel}</>;
}

/* -------------------------------------------------------------------------- */
/*  Action Button (animated progress + real API call)                           */
/* -------------------------------------------------------------------------- */

interface ActionStep {
  readonly label: string;
  readonly durationMs: number;
}

function ActionButton({
  label,
  steps,
  onExecute,
  doneLabel = 'Done',
  variant = 'default',
}: {
  label: string;
  steps: readonly ActionStep[];
  onExecute: () => Promise<void>;
  doneLabel?: string | undefined;
  variant?: 'default' | 'primary' | 'success' | undefined;
}) {
  const [phase, setPhase] = useState<'idle' | 'animating' | 'calling' | 'done'>('idle');
  const [progress, setProgress] = useState(0);
  const [stepIndex, setStepIndex] = useState(0);

  const colors = {
    default: 'border-edge bg-surface-raised text-ink hover:bg-surface-overlay',
    primary: 'border-profit bg-profit text-white hover:bg-profit/90',
    success: 'border-emerald-400 bg-emerald-600 text-white hover:bg-emerald-700',
  };

  async function run() {
    setPhase('animating');
    setProgress(0);
    setStepIndex(0);

    const totalDuration = steps.reduce((s, st) => s + st.durationMs, 0);
    let elapsed = 0;

    for (let i = 0; i < steps.length; i++) {
      const step = steps[i];
      if (step === undefined) continue;
      setStepIndex(i);

      const stepDuration = step.durationMs;
      const start = performance.now();
      const stepStart = elapsed;

      await new Promise<void>((resolve) => {
        function tick() {
          const now = performance.now();
          const dt = now - start;
          const stepPct = Math.min(dt / stepDuration, 1);
          const overallPct = ((stepStart + stepDuration * stepPct) / totalDuration) * 100;
          setProgress(Math.round(overallPct));
          if (stepPct < 1) requestAnimationFrame(tick);
          else resolve();
        }
        requestAnimationFrame(tick);
      });

      elapsed += stepDuration;
    }

    setPhase('calling');
    setProgress(100);

    try {
      await onExecute();
    } finally {
      setPhase('done');
      setTimeout(() => {
        setPhase('idle');
        setProgress(0);
        setStepIndex(0);
      }, 1200);
    }
  }

  if (phase === 'idle') {
    return (
      <button
        type="button"
        onClick={() => void run()}
        className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors ${colors[variant]}`}
      >
        <PlayIcon />
        {label}
      </button>
    );
  }

  const currentStep = steps[stepIndex];

  return (
    <div className="flex flex-col gap-1.5 min-w-[220px]">
      <div className="flex items-center justify-between text-xs">
        <span className="font-medium text-ink">
          {phase === 'done'
            ? doneLabel
            : phase === 'calling'
              ? 'Saving…'
              : (currentStep?.label ?? label)}
        </span>
        <span className="text-ink-faint">{progress}%</span>
      </div>
      <div className="h-1.5 rounded-full bg-surface-overlay overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-150 ${phase === 'done' ? 'bg-profit' : 'bg-revenue'}`}
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Stage 1: Collect Hours                                                     */
/* -------------------------------------------------------------------------- */

type CollectTab = 'received' | 'missing';

/** @deprecated Collect is now folded into VerifyPanel. Kept for reference. */
export function CollectPanel({
  onRefresh,
  periodId,
}: {
  onRefresh?: () => void;
  periodId?: string;
}) {
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState<CollectTab>('received');
  const [uploading, setUploading] = useState(false);
  const [collecting, setCollecting] = useState(false);
  const [uploadResult, setUploadResult] = useState<import('../api').IngestionResult | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const { data, error, loading, reload } = useAsyncData<CollectStageData>(
    () => api.stageCollect(periodId),
    [periodId],
    'Could not load timesheet collection data.',
  );

  if (error !== null) return <PanelError message={error} />;
  if (loading || data === null) return <PanelLoading label="Loading timesheet data…" />;

  const missing = data.contractors.filter(
    (c) =>
      c.status === 'missing' &&
      (search === '' ||
        c.name.toLowerCase().includes(search.toLowerCase()) ||
        c.clientName.toLowerCase().includes(search.toLowerCase())),
  );

  const received = data.contractors.filter(
    (c) =>
      c.status === 'received' &&
      (search === '' ||
        c.name.toLowerCase().includes(search.toLowerCase()) ||
        c.clientName.toLowerCase().includes(search.toLowerCase())),
  );

  const pct = data.summary.total > 0 ? (data.summary.received / data.summary.total) * 100 : 0;

  const handleFileUpload = async (file: File) => {
    setUploading(true);
    setUploadError(null);
    setUploadResult(null);
    try {
      const result = await api.uploadTimesheet(file, data.period.periodId);
      setUploadResult(result);
      reload();
      onRefresh?.();
    } catch (e) {
      setUploadError(e instanceof Error ? e.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="rounded-lg border border-edge bg-surface-base">
      <div className="border-b border-edge px-4 py-3">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold text-ink">Collect Hours</p>
            <p className="text-xs text-ink-muted mt-0.5">{data.period.periodLabel}</p>
          </div>
          <div className="w-48">
            <SearchBar value={search} onChange={setSearch} placeholder="Filter contractors…" />
          </div>
        </div>
        <div className="mt-3">
          <div className="flex items-center justify-between text-xs text-ink-muted mb-1">
            <span>
              {data.summary.received} of {data.summary.total} timesheets received
            </span>
            <span>{Math.round(pct)}%</span>
          </div>
          <div className="h-2 rounded-full bg-surface-overlay overflow-hidden">
            <div
              className="h-full rounded-full bg-profit transition-all duration-500"
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          {/* Upload Timesheet Report */}
          <label
            className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors cursor-pointer
              ${
                uploading
                  ? 'border-edge bg-surface-overlay text-ink-faint cursor-wait'
                  : 'border-indigo-300 bg-indigo-50 text-indigo-700 hover:bg-indigo-100'
              }`}
          >
            {uploading ? (
              <svg className="h-3 w-3 animate-spin" viewBox="0 0 24 24" fill="none">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                />
              </svg>
            ) : (
              <svg
                className="h-3 w-3"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                />
              </svg>
            )}
            {uploading ? 'Uploading…' : 'Upload Timesheet Report'}
            <input
              type="file"
              accept=".xlsx,.xls"
              className="hidden"
              disabled={uploading}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleFileUpload(file);
                e.target.value = '';
              }}
            />
          </label>

          {/* Sync from Oorwin API — centralized incremental sync */}
          <button
            type="button"
            disabled={collecting}
            onClick={async () => {
              setCollecting(true);
              setUploadError(null);
              setUploadResult(null);
              try {
                const syncResponse = await api.syncOorwinTimesheets();
                if (syncResponse.outcome === 'skipped') {
                  setUploadError(syncResponse.reason);
                } else {
                  // Poll for completion
                  let attempts = 0;
                  const maxAttempts = 150; // 5 minutes at 2s intervals
                  const poll = async () => {
                    const status = await api.oorwinSyncStatus();
                    if (status.currentSync === null || status.currentSync.status !== 'running') {
                      // Sync complete
                      reload();
                      onRefresh?.();
                      return;
                    }
                    attempts++;
                    if (attempts < maxAttempts) {
                      setTimeout(() => {
                        poll().catch(() => {});
                      }, 2000);
                    }
                  };
                  // Start polling after a short delay
                  setTimeout(() => {
                    poll().catch(() => {});
                  }, 2000);
                }
              } catch (e) {
                setUploadError(e instanceof Error ? e.message : 'Oorwin sync failed');
              } finally {
                setCollecting(false);
              }
            }}
            className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors
              ${
                collecting
                  ? 'border-edge bg-surface-overlay text-ink-faint cursor-wait'
                  : 'border-teal-300 bg-teal-50 text-teal-700 hover:bg-teal-100'
              }`}
          >
            {collecting ? (
              <svg className="h-3 w-3 animate-spin" viewBox="0 0 24 24" fill="none">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                />
              </svg>
            ) : (
              <svg
                className="h-3 w-3"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
            )}
            {collecting ? 'Syncing from Oorwin…' : 'Sync from Oorwin'}
          </button>
        </div>

        {/* Upload result feedback */}
        {uploadResult !== null && (
          <div className="mt-3 rounded-lg border border-profit/30 bg-profit/5 px-3 py-2.5">
            <div className="flex items-center gap-2 mb-1">
              <svg
                className="h-4 w-4 text-profit"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
              <span className="text-sm font-medium text-profit">Upload complete</span>
            </div>
            <div className="grid grid-cols-5 gap-2 text-xs text-ink-muted">
              <span>{uploadResult.created} created</span>
              <span>{uploadResult.updated} updated</span>
              <span>{uploadResult.unchanged} unchanged</span>
              <span>{uploadResult.skipped} skipped</span>
              <span>{uploadResult.conflicts.length} conflicts</span>
            </div>
            {uploadResult.errors.length > 0 && (
              <div className="mt-2 text-xs text-caution">
                {uploadResult.errors.length} row(s) had errors:
                <ul className="mt-1 space-y-0.5 ml-4 list-disc">
                  {uploadResult.errors.slice(0, 5).map((err, i) => (
                    <li key={i}>
                      Row {err.row ?? '?'}: {err.message}
                    </li>
                  ))}
                  {uploadResult.errors.length > 5 && (
                    <li>…and {uploadResult.errors.length - 5} more</li>
                  )}
                </ul>
              </div>
            )}
            {uploadResult.conflicts.length > 0 && (
              <div className="mt-2 text-xs text-amber-600">
                {uploadResult.conflicts.length} conflict(s):
                <ul className="mt-1 space-y-0.5 ml-4 list-disc">
                  {uploadResult.conflicts.slice(0, 3).map((c, i) => (
                    <li key={i}>
                      {c.workerRef} ({c.workDate}): existing {c.existingSource} {c.existingHours}h,
                      skipped incoming {c.incomingHours}h (
                      {c.reason === 'manual_override' ? 'manual override' : 'lower priority'})
                    </li>
                  ))}
                  {uploadResult.conflicts.length > 3 && (
                    <li>…and {uploadResult.conflicts.length - 3} more</li>
                  )}
                </ul>
              </div>
            )}
          </div>
        )}

        {uploadError !== null && (
          <div className="mt-3 rounded-lg border border-caution/30 bg-caution/5 px-3 py-2 text-xs text-caution">
            Upload failed: {uploadError}
          </div>
        )}
      </div>

      <div className="border-b border-edge px-4 pt-2 flex gap-4">
        <button
          type="button"
          onClick={() => setActiveTab('received')}
          className={`pb-2 text-xs font-medium border-b-2 transition-colors ${
            activeTab === 'received'
              ? 'border-profit text-profit'
              : 'border-transparent text-ink-faint hover:text-ink-muted'
          }`}
        >
          Received ({received.length})
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('missing')}
          className={`pb-2 text-xs font-medium border-b-2 transition-colors ${
            activeTab === 'missing'
              ? 'border-caution text-caution'
              : 'border-transparent text-ink-faint hover:text-ink-muted'
          }`}
        >
          Missing ({missing.length})
        </button>
      </div>

      {activeTab === 'received' &&
        (received.length > 0 ? (
          <Table
            head={[
              { label: 'Contractor' },
              { label: 'Customer' },
              { label: 'Week Ending' },
              { label: 'Hours', align: 'right' },
              { label: 'Source' },
            ]}
          >
            {received.map((c, i) => (
              <Row key={`${c.id}-${c.weekEnding ?? i}`}>
                <Cell>{c.name}</Cell>
                <Cell>{c.clientName}</Cell>
                <Cell muted>{c.weekEnding ? shortDate(c.weekEnding) : '—'}</Cell>
                <Cell align="right" mono>
                  {c.hoursSubmitted !== null ? hours(c.hoursSubmitted) : '—'}
                </Cell>
                <Cell muted>{c.source ?? '—'}</Cell>
              </Row>
            ))}
          </Table>
        ) : (
          <p className="px-4 py-6 text-sm text-ink-muted text-center">
            No timesheets received yet. Click Sync from Oorwin or upload a report.
          </p>
        ))}

      {activeTab === 'missing' &&
        (missing.length > 0 ? (
          <Table
            head={[
              { label: 'Contractor' },
              { label: 'Customer' },
              { label: 'Last Submitted' },
              { label: 'Email' },
              { label: 'Phone' },
              { label: '' },
            ]}
          >
            {missing.map((c) => (
              <MissingRow key={c.id} contractor={c} />
            ))}
          </Table>
        ) : (
          <p className="px-4 py-6 text-sm text-profit text-center">
            All contractors have submitted timesheets for this period.
          </p>
        ))}
    </div>
  );
}

function MissingRow({ contractor: c }: { contractor: CollectStageContractor }) {
  return (
    <tr className="border-b border-edge/50 last:border-0 bg-caution/5">
      <Cell>{c.name}</Cell>
      <Cell>{c.clientName}</Cell>
      <Cell muted>{c.lastSubmitted ? shortDate(c.lastSubmitted) : '—'}</Cell>
      <Cell>
        {c.email ? (
          <a href={`mailto:${c.email}`} className="text-xs text-indigo-600 hover:underline">
            {c.email}
          </a>
        ) : (
          <span className="text-xs text-ink-faint">—</span>
        )}
      </Cell>
      <Cell>
        {c.phone ? (
          <a href={`tel:${c.phone}`} className="text-xs text-indigo-600 hover:underline">
            {c.phone}
          </a>
        ) : (
          <span className="text-xs text-ink-faint">—</span>
        )}
      </Cell>
      <Cell>
        <button
          disabled
          className="rounded bg-ink-faint/20 px-2 py-1 text-[10px] font-medium text-ink-muted cursor-not-allowed"
        >
          Send Reminder
        </button>
      </Cell>
    </tr>
  );
}

/* -------------------------------------------------------------------------- */
/*  Stage 2: Verify Hours                                                      */
/* -------------------------------------------------------------------------- */

function VerifyActionCell({
  onVerify,
  onReject,
}: {
  onVerify: (note?: string) => void;
  onReject: (note?: string) => void;
}) {
  const [action, setAction] = useState<'verify' | 'reject' | null>(null);
  const [note, setNote] = useState('');

  if (action !== null) {
    return (
      <div className="flex flex-col gap-1" onClick={(e) => e.stopPropagation()}>
        <input
          type="text"
          placeholder="Note (optional)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          className="w-full rounded border border-edge bg-surface-base px-2 py-1 text-[10px]"
          autoFocus
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              (action === 'verify' ? onVerify : onReject)(note || undefined);
              setAction(null);
              setNote('');
            }
            if (e.key === 'Escape') {
              setAction(null);
              setNote('');
            }
          }}
        />
        <div className="flex gap-1">
          <button
            type="button"
            onClick={() => {
              (action === 'verify' ? onVerify : onReject)(note || undefined);
              setAction(null);
              setNote('');
            }}
            className={`rounded px-2 py-0.5 text-[10px] font-medium ${
              action === 'verify' ? 'bg-profit/10 text-profit' : 'bg-red-50 text-red-600'
            }`}
          >
            {action === 'verify' ? 'Confirm' : 'Confirm Reject'}
          </button>
          <button
            type="button"
            onClick={() => {
              setAction(null);
              setNote('');
            }}
            className="rounded px-2 py-0.5 text-[10px] text-ink-faint hover:text-ink-muted"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setAction('verify');
        }}
        className="rounded bg-profit/10 px-2 py-1 text-[10px] font-medium text-profit hover:bg-profit/20 transition-colors"
      >
        Verify
      </button>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setAction('reject');
        }}
        className="rounded bg-red-50 px-2 py-1 text-[10px] font-medium text-red-600 hover:bg-red-100 transition-colors"
      >
        Reject
      </button>
    </div>
  );
}

type VerifyFilter = 'all' | 'unverified' | 'verified' | 'flagged' | 'rejected';

function VerifyPanel({ onRefresh, periodId }: { onRefresh?: () => void; periodId?: string }) {
  const [search, setSearch] = useState('');
  // Tabs removed — single list with zero-hour workers included
  const [statusFilter, setStatusFilter] = useState<VerifyFilter>('all');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [selectedWorker, setSelectedWorker] = useState<string | null>(null);
  const [selectedWeekEnding, setSelectedWeekEnding] = useState<string | null>(null);
  const [workerDetail, setWorkerDetail] = useState<VerifyWorkerDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [batchRunning, setBatchRunning] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [dailyHours, setDailyHours] = useState<
    | {
        date: string;
        dayName: string;
        regular: string;
        overtime: string;
        holiday: string;
        vacation: string;
        total: string;
      }[]
    | null
  >(null);
  const [dailyLoading, setDailyLoading] = useState(false);

  const { data, error, loading, reload } = useAsyncData<VerifyStageData>(
    () => api.stageVerify(periodId),
    [periodId],
    'Could not load verification data.',
  );

  const accessor = useCallback((row: VerifyStageContractor, key: string): unknown => {
    const map: Record<string, unknown> = {
      name: row.name,
      client: row.clientName,
      week: row.weekEnding,
      regular: row.regular,
      overtime: row.overtime,
      holiday: row.holiday,
      vacation: row.vacation,
      total: row.totalHours,
      status: row.verificationStatus,
    };
    return map[key];
  }, []);

  const filtered =
    data !== null
      ? data.contractors.filter((c) => {
          if (
            search !== '' &&
            !c.name.toLowerCase().includes(search.toLowerCase()) &&
            !c.clientName.toLowerCase().includes(search.toLowerCase())
          )
            return false;
          if (statusFilter === 'all') return true;
          if (statusFilter === 'unverified')
            return c.verificationStatus === 'pending' && !c.flagged;
          if (statusFilter === 'verified')
            return (
              c.verificationStatus === 'verified' ||
              c.verificationStatus === 'auto_verified' ||
              c.verificationStatus === 'verified_with_adjustments'
            );
          if (statusFilter === 'flagged') return c.flagged && c.verificationStatus === 'pending';
          if (statusFilter === 'rejected') return c.verificationStatus === 'rejected';
          return true;
        })
      : [];

  const { sorted, sortKey, sortDirection, onSort } = useSortedRows(filtered, accessor);

  if (error !== null) return <PanelError message={error} />;
  if (loading || data === null) return <PanelLoading label="Loading verification data…" />;

  /** Selection key: unique per worker per week. */
  const selKey = (c: VerifyStageContractor) => `${c.id}-${c.weekEnding}`;

  const allFilteredSelected = sorted.length > 0 && sorted.every((c) => selectedIds.has(selKey(c)));

  function toggleSelectAll() {
    if (allFilteredSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(sorted.map(selKey)));
    }
  }

  function toggleSelect(key: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  /** Group sorted rows by worker for visual stacking. */
  type WorkerGroup = { workerId: string; rows: VerifyStageContractor[] };
  const grouped: WorkerGroup[] = [];
  for (const c of sorted) {
    const last = grouped[grouped.length - 1];
    if (last !== undefined && last.workerId === c.id) {
      last.rows.push(c);
    } else {
      grouped.push({ workerId: c.id, rows: [c] });
    }
  }

  async function openDetail(workerId: string, weekEnding?: string, payPeriodId?: string) {
    setSelectedWorker(workerId);
    setSelectedWeekEnding(weekEnding ?? null);
    setDetailLoading(true);
    setDailyHours(null);
    try {
      const detail = await api.verifyWorkerDetail(workerId, payPeriodId ?? periodId);
      setWorkerDetail(detail);
    } catch {
      setWorkerDetail(null);
    } finally {
      setDetailLoading(false);
    }
    if (weekEnding) {
      setDailyLoading(true);
      try {
        const result = await api.dailyHours(workerId, weekEnding);
        setDailyHours(result.days as typeof dailyHours);
      } catch {
        setDailyHours(null);
      } finally {
        setDailyLoading(false);
      }
    }
  }

  async function handleVerify(workerId: string, payPeriodId?: string, note?: string) {
    await api.batchVerify([workerId], 'verified', payPeriodId ?? periodId, note);
    setSelectedWorker(null);
    setWorkerDetail(null);
    reload();
    onRefresh?.();
  }

  async function handleReject(workerId: string, payPeriodId?: string, note?: string) {
    await api.batchVerify([workerId], 'rejected', payPeriodId ?? periodId, note);
    setSelectedWorker(null);
    setWorkerDetail(null);
    reload();
    onRefresh?.();
  }

  async function handleBatchVerify() {
    if (selectedIds.size === 0) return;
    setBatchRunning(true);
    try {
      // Group selected keys by payPeriodId and send one call per period
      const byPeriod = new Map<string, string[]>();
      for (const key of selectedIds) {
        const row = sorted.find((c) => selKey(c) === key);
        if (!row) continue;
        const pid = row.payPeriodId;
        const list = byPeriod.get(pid) ?? [];
        list.push(row.id);
        byPeriod.set(pid, list);
      }
      for (const [pid, workerIds] of byPeriod) {
        await api.batchVerify(workerIds, 'verified', pid);
      }
      setSelectedIds(new Set());
      reload();
      onRefresh?.();
    } finally {
      setBatchRunning(false);
    }
  }

  function statusLabel(
    c: VerifyStageContractor,
  ): 'verified' | 'rejected' | 'flagged' | 'auto' | 'adjusted' | 'pending' {
    if (c.verificationStatus === 'verified') return 'verified';
    if (c.verificationStatus === 'verified_with_adjustments') return 'adjusted';
    if (c.verificationStatus === 'rejected') return 'rejected';
    if (c.verificationStatus === 'auto_verified') return 'auto';
    if (c.flagged) return 'flagged';
    return 'pending';
  }

  const filterOptions: { value: VerifyFilter; label: string }[] = [
    { value: 'all', label: 'All' },
    { value: 'unverified', label: 'Unverified' },
    { value: 'verified', label: 'Verified' },
    { value: 'flagged', label: 'Flagged' },
    { value: 'rejected', label: 'Rejected' },
  ];

  return (
    <div className="rounded-lg border border-edge bg-surface-base">
      {/* Toolbar — sync, batch verify, filter, search */}
      <div className="border-b border-edge px-4 py-2">
        <div className="flex items-center justify-between gap-3">
          <span className="text-xs text-ink-faint whitespace-nowrap">
            {String(
              data.contractors.length > 0
                ? [...new Set(data.contractors.map((c) => c.id))].length
                : 0,
            )}{' '}
            contractors, {String(data.contractors.length)} rows
          </span>
          <div className="flex items-center gap-3">
            <button
              type="button"
              disabled={syncing}
              onClick={async () => {
                setSyncing(true);
                setSyncError(null);
                try {
                  const resp = await api.syncOorwinTimesheets();
                  if (resp.outcome === 'skipped') {
                    setSyncError(resp.reason);
                  } else {
                    setTimeout(() => {
                      reload();
                      onRefresh?.();
                      setSyncing(false);
                    }, 5000);
                    return;
                  }
                } catch (e) {
                  setSyncError(e instanceof Error ? e.message : 'Sync failed');
                }
                setSyncing(false);
              }}
              className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors ${
                syncing
                  ? 'border-edge bg-surface-overlay text-ink-faint cursor-wait'
                  : 'border-teal-300 bg-teal-50 text-teal-700 hover:bg-teal-100'
              }`}
            >
              {syncing ? 'Syncing…' : 'Sync from Oorwin'}
            </button>
            <button
              type="button"
              disabled={selectedIds.size === 0 || batchRunning}
              onClick={handleBatchVerify}
              className={`rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors ${
                selectedIds.size > 0 && !batchRunning
                  ? 'border-profit bg-profit/10 text-profit hover:bg-profit/20'
                  : 'border-edge bg-surface-overlay text-ink-faint cursor-not-allowed'
              }`}
            >
              {batchRunning ? 'Verifying…' : `Verify Selected (${selectedIds.size})`}
            </button>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value as VerifyFilter);
                setSelectedIds(new Set());
              }}
              className="rounded-lg border border-edge bg-surface-raised px-2 py-1.5 text-xs text-ink"
            >
              {filterOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <div className="w-48">
              <SearchBar value={search} onChange={setSearch} placeholder="Filter contractors…" />
            </div>
          </div>
        </div>
        {(() => {
          const nonFlaggedPending = data.contractors.filter(
            (c) => c.verificationStatus === 'pending' && !c.flagged,
          );
          if (nonFlaggedPending.length === 0) return null;
          return (
            <div className="mt-3">
              <ActionButton
                label={`Verify All Within Threshold (${String(nonFlaggedPending.length)})`}
                steps={[
                  { label: 'Checking variance thresholds…', durationMs: 600 },
                  {
                    label: `Auto-verifying ${String(nonFlaggedPending.length)} contractors…`,
                    durationMs: 1000,
                  },
                ]}
                onExecute={async () => {
                  const byPeriod = new Map<string, string[]>();
                  for (const c of nonFlaggedPending) {
                    const list = byPeriod.get(c.payPeriodId) ?? [];
                    list.push(c.id);
                    byPeriod.set(c.payPeriodId, list);
                  }
                  for (const [pid, ids] of byPeriod) {
                    await api.batchVerify(ids, 'auto_verified', pid);
                  }
                  reload();
                  onRefresh?.();
                }}
                doneLabel={`${String(nonFlaggedPending.length)} auto-verified`}
                variant="success"
              />
            </div>
          );
        })()}

        {syncError && (
          <div className="mt-2 rounded-lg border border-caution/30 bg-caution/5 px-3 py-2 text-xs text-caution">
            {syncError}
          </div>
        )}
      </div>

      {/* Bulk approve portal submissions banner */}
      {(() => {
        const pendingSubmissionRows = data.contractors.filter(
          (c) => c.submissionStatus === 'pending' && c.submissionIds && c.submissionIds.length > 0,
        );
        if (pendingSubmissionRows.length === 0) return null;
        const allIds = pendingSubmissionRows.flatMap((c) => c.submissionIds ?? []);
        return (
          <div className="border-b border-edge bg-amber-50/30 px-4 py-2 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center rounded-full bg-caution/10 px-2 py-0.5 text-xs font-medium text-caution">
                {String(allIds.length)} pending
              </span>
              <span className="text-xs text-ink-muted">
                Portal submissions awaiting approval (included in grid below)
              </span>
            </div>
            <button
              type="button"
              disabled={batchRunning}
              onClick={async () => {
                setBatchRunning(true);
                try {
                  await api.bulkApproveSubmissions(allIds);
                  reload();
                  onRefresh?.();
                } finally {
                  setBatchRunning(false);
                }
              }}
              className="rounded-lg border border-profit bg-profit/10 px-3 py-1 text-xs font-medium text-profit hover:bg-profit/20 transition-colors"
            >
              {batchRunning ? 'Processing…' : `Approve All (${String(allIds.length)})`}
            </button>
          </div>
        );
      })()}

      <div className="overflow-auto max-h-[600px]">
        <table className="w-full border-collapse text-sm">
          <thead className="sticky top-0 z-10 bg-surface-raised">
            <tr>
              {(
                [
                  { label: '', sortKey: undefined, align: 'left' as const },
                  { label: 'Contractor', sortKey: 'name', align: 'left' as const },
                  { label: 'Customer', sortKey: 'client', align: 'left' as const },
                  { label: 'Week', sortKey: 'week', align: 'left' as const },
                  { label: 'Regular', sortKey: 'regular', align: 'right' as const },
                  { label: 'OT', sortKey: 'overtime', align: 'right' as const },
                  { label: 'Holiday', sortKey: 'holiday', align: 'right' as const },
                  { label: 'Vacation', sortKey: 'vacation', align: 'right' as const },
                  { label: 'Total', sortKey: 'total', align: 'right' as const },
                  { label: 'Status', sortKey: 'status', align: 'left' as const },
                  { label: '', sortKey: undefined, align: 'left' as const },
                ] as const
              ).map((col) => (
                <th
                  key={col.label || 'action'}
                  scope="col"
                  className={`px-5 py-2.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-ink-muted border-b-2 border-edge ${
                    col.align === 'right' ? 'text-right' : 'text-left'
                  } ${col.sortKey ? 'cursor-pointer select-none hover:text-ink' : ''}`}
                  onClick={col.sortKey ? () => onSort(col.sortKey!) : undefined}
                >
                  {col.label}
                  {col.sortKey && sortKey === col.sortKey && (
                    <span className="ml-1">{sortDirection === 'asc' ? '▲' : '▼'}</span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-edge/50 bg-surface-overlay/30">
              <Cell>
                <input
                  type="checkbox"
                  checked={allFilteredSelected && sorted.length > 0}
                  onChange={toggleSelectAll}
                  className="h-3.5 w-3.5 rounded border-edge accent-profit"
                />
              </Cell>
              <td colSpan={10} className="px-3 py-1.5 text-xs text-ink-faint">
                {grouped.length} contractor{grouped.length !== 1 ? 's' : ''}, {sorted.length} row
                {sorted.length !== 1 ? 's' : ''}
              </td>
            </tr>
            {grouped.map((group, groupIdx) =>
              group.rows.map((c, rowIdx) => {
                const status = statusLabel(c);
                const key = selKey(c);
                const isSelected = selectedIds.has(key);
                const isFirst = rowIdx === 0;
                const isLast = rowIdx === group.rows.length - 1;
                const groupBg = groupIdx % 2 === 0 ? 'bg-white' : 'bg-caution/5';
                return (
                  <tr
                    key={key}
                    className={[
                      'cursor-pointer transition-colors hover:bg-surface-overlay/60',
                      groupBg,
                      isLast ? 'border-b-2 border-edge' : 'border-b border-edge/30',
                    ].join(' ')}
                    onClick={() => openDetail(c.id, c.weekEnding, c.payPeriodId)}
                  >
                    <Cell>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) => {
                          e.stopPropagation();
                          toggleSelect(key);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        className="h-3.5 w-3.5 rounded border-edge accent-profit"
                      />
                    </Cell>
                    <Cell>{isFirst ? c.name : ''}</Cell>
                    <Cell muted>{isFirst ? c.clientName : ''}</Cell>
                    <Cell muted>{c.weekEnding ? shortDate(c.weekEnding) : '—'}</Cell>
                    <Cell align="right" mono>
                      {hours(c.regular)}
                    </Cell>
                    <Cell align="right" mono>
                      {hours(c.overtime)}
                    </Cell>
                    <Cell align="right" mono>
                      {hours(c.holiday)}
                    </Cell>
                    <Cell align="right" mono>
                      {hours(c.vacation)}
                    </Cell>
                    <Cell align="right" mono>
                      <span
                        className={
                          status === 'flagged' ? 'text-caution font-semibold' : 'font-semibold'
                        }
                      >
                        {hours(c.totalHours)}
                      </span>
                    </Cell>
                    <Cell>
                      <div className="flex items-center gap-1">
                        {status === 'flagged' ? (
                          <span
                            className="inline-flex items-center gap-1 rounded-full bg-caution/10 px-2 py-0.5 text-xs font-medium text-caution"
                            title={c.flagReason ?? undefined}
                          >
                            <FlagIcon /> Flagged
                          </span>
                        ) : status === 'rejected' ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-600">
                            Rejected
                          </span>
                        ) : status === 'pending' ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-surface-overlay px-2 py-0.5 text-xs font-medium text-ink-muted">
                            Pending
                          </span>
                        ) : status === 'adjusted' ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
                            <CheckIcon /> Adjusted
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 rounded-full bg-profit/10 px-2 py-0.5 text-xs font-medium text-profit">
                            <CheckIcon /> Verified
                          </span>
                        )}
                        {c.submissionStatus === 'pending' && (
                          <span
                            className="inline-flex items-center rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700"
                            title="Includes unapproved portal submissions"
                          >
                            Portal
                          </span>
                        )}
                      </div>
                    </Cell>
                    <Cell>
                      <div className="flex items-center gap-1">
                        {(status === 'flagged' || status === 'pending') && (
                          <VerifyActionCell
                            onVerify={(note) => void handleVerify(c.id, c.payPeriodId, note)}
                            onReject={(note) => void handleReject(c.id, c.payPeriodId, note)}
                          />
                        )}
                        {c.submissionStatus === 'pending' &&
                          c.submissionIds &&
                          c.submissionIds.length > 0 && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                void (async () => {
                                  await api.bulkApproveSubmissions(c.submissionIds!);
                                  reload();
                                  onRefresh?.();
                                })();
                              }}
                              className="rounded bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-700 hover:bg-amber-200"
                            >
                              Approve Portal
                            </button>
                          )}
                      </div>
                    </Cell>
                  </tr>
                );
              }),
            )}
          </tbody>
        </table>
      </div>

      <Modal
        open={selectedWorker !== null}
        onClose={() => {
          setSelectedWorker(null);
          setWorkerDetail(null);
        }}
        title={workerDetail?.worker.name ?? 'Loading…'}
        subtitle={
          workerDetail !== null
            ? `${workerDetail.client} · ${workerDetail.worker.workerType === 'contractor_1099' ? '1099' : 'W2'}`
            : undefined
        }
        size="lg"
      >
        {detailLoading ? (
          <div className="flex items-center gap-3 py-10 text-sm text-ink-faint">
            <span className="h-3 w-3 animate-pulse rounded-full bg-revenue-dim" />
            Loading worker details…
          </div>
        ) : workerDetail === null ? (
          <p className="text-sm text-caution">Could not load worker details.</p>
        ) : (
          <VerifyDetailContent
            detail={workerDetail}
            onVerify={() => handleVerify(workerDetail.worker.id)}
            onReject={() => handleReject(workerDetail.worker.id)}
            onReloadDetail={() => openDetail(workerDetail.worker.id)}
            onRefreshList={() => {
              reload();
              onRefresh?.();
            }}
            status={(() => {
              const c = data.contractors.find((x) => x.id === workerDetail.worker.id);
              if (c === undefined) return 'flagged' as const;
              if (c.verificationStatus === 'verified') return 'verified' as const;
              if (c.verificationStatus === 'rejected') return 'rejected' as const;
              if (c.verificationStatus === 'auto_verified') return 'verified' as const;
              if (c.verificationStatus === 'verified_with_adjustments') return 'verified' as const;
              if (c.flagged) return 'flagged' as const;
              return 'pending' as const;
            })()}
            weekEnding={selectedWeekEnding}
            dailyHours={dailyHours}
            dailyLoading={dailyLoading}
            onRefreshDaily={async () => {
              if (!selectedWeekEnding) return;
              setDailyLoading(true);
              try {
                const result = await api.dailyHours(
                  workerDetail.worker.id,
                  selectedWeekEnding,
                  true,
                );
                setDailyHours(result.days as typeof dailyHours);
              } catch {
                setDailyHours(null);
              } finally {
                setDailyLoading(false);
              }
            }}
            {...(periodId !== undefined ? { periodId } : {})}
          />
        )}
      </Modal>
    </div>
  );
}

type DailyHourRow = {
  date: string;
  dayName: string;
  regular: string;
  overtime: string;
  holiday: string;
  vacation: string;
  total: string;
};

function VerifyDetailContent({
  detail,
  onVerify,
  onReject,
  status,
  weekEnding,
  dailyHours: dailyHoursData,
  dailyLoading,
  onRefreshDaily,
}: {
  detail: VerifyWorkerDetail;
  onVerify: () => void;
  onReject: () => void;
  onReloadDetail: () => void;
  onRefreshList: () => void;
  status: 'verified' | 'rejected' | 'flagged' | 'pending';
  periodId?: string;
  weekEnding: string | null;
  dailyHours: DailyHourRow[] | null;
  dailyLoading: boolean;
  onRefreshDaily: () => void;
}) {
  return (
    <div className="space-y-5">
      {/* Actions bar — right-aligned at the top */}
      <div className="flex items-center justify-between">
        <div className="space-y-1 text-sm">
          {detail.worker.email !== null && (
            <a
              href={`mailto:${detail.worker.email}`}
              className="text-revenue hover:underline text-xs block"
            >
              {detail.worker.email}
            </a>
          )}
          {detail.worker.phone !== null && (
            <a
              href={`tel:${detail.worker.phone}`}
              className="text-revenue hover:underline text-xs block"
            >
              {detail.worker.phone}
            </a>
          )}
          <p className="text-xs text-ink-muted">
            {detail.client}
            {detail.assignmentStart !== null && ` · Since ${shortDate(detail.assignmentStart)}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {status === 'verified' ? (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-profit/10 px-3 py-1.5 text-xs font-medium text-profit">
              <CheckIcon /> Verified
            </span>
          ) : status === 'rejected' ? (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-red-100 px-3 py-1.5 text-xs font-medium text-red-600">
              Rejected
            </span>
          ) : (
            <>
              <button
                type="button"
                onClick={onVerify}
                className="rounded-lg border border-profit bg-profit/10 px-4 py-2 text-sm font-medium text-profit hover:bg-profit/20 transition-colors"
              >
                Verify Hours
              </button>
              <button
                type="button"
                onClick={onReject}
                className="rounded-lg border border-red-300 bg-red-50 px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-100 transition-colors"
              >
                Reject
              </button>
            </>
          )}
          {detail.worker.email !== null && (
            <a
              href={`mailto:${detail.worker.email}`}
              className="rounded-lg border border-edge px-4 py-2 text-sm font-medium text-ink-muted hover:text-ink hover:border-edge-strong transition-colors"
            >
              Contact
            </a>
          )}
        </div>
      </div>

      {/* Weekly Hours (from time entries) */}
      {detail.dailyBreakdown.length > 0 && (
        <section>
          <h3 className="text-xs font-medium text-ink-faint uppercase tracking-wide mb-2">
            Week ending{' '}
            {weekEnding ? shortDate(weekEnding) : shortDate(detail.dailyBreakdown[0]!.date)}
          </h3>
          <table className="w-full text-xs">
            <thead>
              <tr className="text-ink-faint border-b border-edge">
                <th className="text-left py-1.5 font-medium">Date</th>
                <th className="text-left py-1.5 font-medium">Type</th>
                <th className="text-right py-1.5 font-medium">Hours</th>
              </tr>
            </thead>
            <tbody>
              {detail.dailyBreakdown.map((d) => (
                <tr key={d.id} className="border-b border-edge/50 last:border-0">
                  <td className="py-1.5 text-ink-muted">{shortDate(d.date)}</td>
                  <td className="py-1.5 text-ink-muted">{d.earningType}</td>
                  <td className="py-1.5 text-right font-mono text-ink">{hours(d.hours)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {/* Daily Hours from Oorwin */}
      {weekEnding && (
        <section>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-medium text-ink-faint uppercase tracking-wide">
              Daily Hours
            </h3>
            {!dailyLoading && (
              <button
                type="button"
                onClick={onRefreshDaily}
                className="text-[10px] text-ink-muted underline hover:text-ink"
              >
                Refresh
              </button>
            )}
          </div>
          {dailyLoading ? (
            <div className="rounded-lg border border-edge bg-surface-overlay/30 px-4 py-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-ink">
                  Fetching daily hours from Oorwin…
                </span>
                <span className="text-[10px] text-ink-faint">Scanning 7 days (Sun–Sat)</span>
              </div>
              <div className="h-2 rounded-full bg-surface-overlay overflow-hidden">
                <div className="h-full rounded-full bg-revenue animate-pulse w-3/4" />
              </div>
              <p className="mt-2 text-[10px] text-ink-faint">
                Querying Oorwin for each day of the week. This may take a few seconds on first load.
              </p>
            </div>
          ) : dailyHoursData !== null ? (
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-edge">
                  <th className="px-3 py-1.5 text-left text-[10px] font-medium uppercase text-ink-faint">
                    Date
                  </th>
                  <th className="px-3 py-1.5 text-left text-[10px] font-medium uppercase text-ink-faint">
                    Day
                  </th>
                  <th className="px-3 py-1.5 text-right text-[10px] font-medium uppercase text-ink-faint">
                    Regular
                  </th>
                  <th className="px-3 py-1.5 text-right text-[10px] font-medium uppercase text-ink-faint">
                    OT
                  </th>
                  <th className="px-3 py-1.5 text-right text-[10px] font-medium uppercase text-ink-faint">
                    Holiday
                  </th>
                  <th className="px-3 py-1.5 text-right text-[10px] font-medium uppercase text-ink-faint">
                    Vacation
                  </th>
                  <th className="px-3 py-1.5 text-right text-[10px] font-medium uppercase text-ink-faint">
                    Total
                  </th>
                </tr>
              </thead>
              <tbody>
                {dailyHoursData.map((d, i) => (
                  <tr
                    key={d.date}
                    className={`border-b border-edge/30 ${Number(d.total) === 0 ? 'text-ink-faint' : ''} ${i % 2 === 1 ? 'bg-surface-overlay/30' : ''}`}
                  >
                    <td className="px-3 py-1.5">{shortDate(d.date)}</td>
                    <td className="px-3 py-1.5">{d.dayName}</td>
                    <td className="px-3 py-1.5 text-right font-mono">{hours(d.regular)}</td>
                    <td className="px-3 py-1.5 text-right font-mono">{hours(d.overtime)}</td>
                    <td className="px-3 py-1.5 text-right font-mono">{hours(d.holiday)}</td>
                    <td className="px-3 py-1.5 text-right font-mono">{hours(d.vacation)}</td>
                    <td className="px-3 py-1.5 text-right font-mono font-semibold">
                      {hours(d.total)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-xs text-ink-faint py-2">Could not load daily hours.</p>
          )}
        </section>
      )}

      {/* Verification Notes */}
      {detail.verificationNotes && detail.verificationNotes.length > 0 && (
        <section>
          <h3 className="text-xs font-medium text-ink-faint uppercase tracking-wide mb-2">
            Verification Notes
          </h3>
          <div className="space-y-2">
            {detail.verificationNotes.map((n, i) => (
              <div key={i} className="rounded-lg border border-edge px-3 py-2 text-xs">
                <div className="flex items-center justify-between mb-1">
                  <span
                    className={`font-medium ${
                      n.status === 'verified' || n.status === 'auto_verified'
                        ? 'text-profit'
                        : n.status === 'rejected'
                          ? 'text-red-600'
                          : 'text-ink-muted'
                    }`}
                  >
                    {n.status.charAt(0).toUpperCase() + n.status.slice(1).replace(/_/g, ' ')} — Week
                    ending {shortDate(n.weekEnding)}
                  </span>
                  <span className="text-[10px] text-ink-faint">
                    {n.verifiedBy ?? 'System'}
                    {n.verifiedAt ? ` · ${new Date(n.verifiedAt).toLocaleString()}` : ''}
                  </span>
                </div>
                {n.note && <p className="text-ink-muted">{n.note}</p>}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Recent History */}
      {detail.history.length > 0 && (
        <section>
          <h3 className="text-xs font-medium text-ink-faint uppercase tracking-wide mb-2">
            Recent History
          </h3>
          <div className="flex items-end gap-2">
            {detail.history.map((h) => {
              const hrsNum = Number(h.hours);
              const maxHrs = Math.max(...detail.history.map((x) => Number(x.hours)), 1);
              const pct = Math.round((hrsNum / maxHrs) * 100);
              return (
                <div key={h.weekEnding} className="flex flex-col items-center gap-1 flex-1">
                  <span className="text-[10px] text-ink-muted font-mono">{hours(h.hours)}</span>
                  <div
                    className="w-full rounded-sm bg-revenue/20 border border-revenue/30 min-h-[4px]"
                    style={{ height: `${Math.max(pct, 8)}px` }}
                  />
                  <span className="text-[9px] text-ink-faint">{shortDate(h.weekEnding)}</span>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* PTO Balance */}
      {detail.ptoBalance !== null && (
        <section>
          <h3 className="text-xs font-medium text-ink-faint uppercase tracking-wide mb-2">
            PTO Balance
          </h3>
          <p
            className={`text-sm font-mono font-medium ${Number(detail.ptoBalance) < 0 ? 'text-caution' : 'text-ink'}`}
          >
            {hours(detail.ptoBalance)} hours
          </p>
        </section>
      )}
    </div>
  );
}

function FormulaCell({ formula, negative }: { formula: string; negative?: boolean }) {
  return (
    <code
      className={`text-[10px] font-mono leading-tight ${negative === true ? 'text-caution' : 'text-ink-muted'}`}
    >
      = {formula}
    </code>
  );
}

function TierLadder({ tiers }: { tiers: readonly TierInfo[] }) {
  return (
    <div className="border-b border-edge px-4 py-3">
      <p className="text-xs font-medium text-ink-muted uppercase tracking-wide mb-2">
        Commission Tiers ({tiers[0]?.tierMode ?? 'marginal'})
      </p>
      <div className="flex gap-1 items-end h-8">
        {tiers.map((tier, idx) => {
          const rateNum = Number(tier.rate) * 100;
          const height = 20 + idx * 16;
          return (
            <div key={tier.threshold} className="flex flex-col items-center gap-0.5">
              <span className="text-[10px] text-ink-muted">{rateNum.toFixed(0)}%</span>
              <div
                className="w-12 rounded-sm bg-profit/20 border border-profit/30"
                style={{ height: `${height}%` }}
              />
              <span className="text-[10px] text-ink-faint">{money(tier.threshold)}+</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function RecruiterCard({ recruiter }: { recruiter: CommissionStageData['recruiters'][number] }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="px-4 py-3">
      <button type="button" className="w-full text-left" onClick={() => setExpanded(!expanded)}>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-ink">{recruiter.name}</p>
            <p className="text-xs text-ink-muted mt-0.5">
              Tier: {recruiter.tierReached} · Effective rate: {recruiter.effectiveRate}
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm font-semibold text-revenue">
              {money(recruiter.commissionEarned)}
            </p>
            <p className="text-xs text-ink-faint">on {money(recruiter.totalGp)} GP</p>
          </div>
        </div>
      </button>
      {expanded && (
        <div className="mt-2 ml-2 border-l-2 border-edge pl-3">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-ink-faint">
                <th className="text-left py-1 font-medium">Contractor</th>
                <th className="text-left py-1 font-medium">Customer</th>
                <th className="text-right py-1 font-medium">GP</th>
                <th className="text-right py-1 font-medium">Share</th>
              </tr>
            </thead>
            <tbody>
              {recruiter.contractors.map((c) => (
                <tr key={c.id} className="text-ink-muted">
                  <td className="py-1">{c.name}</td>
                  <td className="py-1">{c.clientName}</td>
                  <td className="py-1 text-right font-mono">{money(c.grossProfit)}</td>
                  <td className="py-1 text-right font-mono">{c.share}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Stage 3: Stage Payroll (with GP + Commissions tabs)                        */
/* -------------------------------------------------------------------------- */

type PayrollTab = 'gp' | 'commissions' | 'payroll';

function PayrollPanel({ onRefresh, periodId }: { onRefresh?: () => void; periodId?: string }) {
  const [activeTab, setActiveTab] = useState<PayrollTab>('gp');
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [heldWorkers, setHeldWorkers] = useState<Map<string, string>>(new Map());
  const [locking, setLocking] = useState(false);
  const [lockError, setLockError] = useState<string | null>(null);

  const { data, error, loading, reload } = useAsyncData<PayrollPreview>(
    () => api.payrollPreview(periodId),
    [periodId],
    'Could not load payroll preview.',
  );

  if (error !== null) return <PanelError message={error} />;
  if (loading || data === null) return <PanelLoading label="Loading payroll staging…" />;

  const isLocked = data.period.status !== 'open';

  const filtered = data.lines.filter(
    (w) =>
      search === '' ||
      w.workerName.toLowerCase().includes(search.toLowerCase()) ||
      w.clientName.toLowerCase().includes(search.toLowerCase()),
  );

  const active = filtered.filter((w) => !heldWorkers.has(w.workerId));
  const held = filtered.filter((w) => heldWorkers.has(w.workerId));

  function deltaTone(pct: string | null): string {
    if (pct === null) return '';
    const abs = Math.abs(Number(pct));
    if (abs > 25) return 'text-caution font-medium';
    if (abs > 10) return 'text-caution';
    return 'text-profit';
  }

  function holdWorker(workerId: string) {
    setHeldWorkers((prev) => new Map(prev).set(workerId, ''));
    setExpandedId(null);
  }

  function releaseWorker(workerId: string) {
    setHeldWorkers((prev) => {
      const next = new Map(prev);
      next.delete(workerId);
      return next;
    });
  }

  async function handleLock() {
    setLocking(true);
    setLockError(null);
    try {
      await api.lockPayroll(periodId);
      reload();
      onRefresh?.();
    } catch (e) {
      setLockError(e instanceof Error ? e.message : 'Lock failed');
    } finally {
      setLocking(false);
    }
  }

  const tabs: { key: PayrollTab; label: string }[] = [
    { key: 'gp', label: 'GP Breakdown' },
    { key: 'commissions', label: 'Commissions' },
    { key: 'payroll', label: 'Payroll Lines' },
  ];

  return (
    <div className="rounded-lg border border-edge bg-surface-base">
      <div className="border-b border-edge px-4 py-3">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold text-ink">Stage Payroll</p>
            <p className="text-xs text-ink-muted mt-0.5">
              Week ending {shortDate(data.period.weekEnding)} · {data.summary.contractorCount}{' '}
              contractors
            </p>
          </div>
          <div className="flex items-center gap-3">
            {activeTab === 'payroll' && (
              <div className="w-48">
                <SearchBar value={search} onChange={setSearch} placeholder="Filter…" />
              </div>
            )}
            {isLocked ? (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-profit/10 px-3 py-1.5 text-xs font-medium text-profit">
                <CheckIcon /> Locked
              </span>
            ) : (
              <button
                type="button"
                disabled={locking || held.length > 0}
                onClick={handleLock}
                className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                  locking || held.length > 0
                    ? 'bg-ink-faint/20 text-ink-muted cursor-not-allowed'
                    : 'border border-profit bg-profit/10 text-profit hover:bg-profit/20'
                }`}
                title={
                  held.length > 0
                    ? 'Release all held workers before locking'
                    : 'Locks the batch and advances to the Approve stage'
                }
              >
                {locking ? 'Locking…' : 'Lock for Approval'}
              </button>
            )}
          </div>
        </div>
        {lockError !== null && <p className="mt-2 text-xs text-caution">{lockError}</p>}
        <div className="mt-3 flex gap-1 rounded-lg border border-edge bg-surface-raised p-0.5 w-fit">
          {tabs.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setActiveTab(t.key)}
              className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${
                activeTab === t.key
                  ? 'bg-surface-overlay text-ink'
                  : 'text-ink-faint hover:text-ink-muted'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {activeTab === 'gp' && <GpTabContent {...(periodId !== undefined ? { periodId } : {})} />}
      {activeTab === 'commissions' && (
        <CommissionTabContent {...(periodId !== undefined ? { periodId } : {})} />
      )}
      {activeTab === 'payroll' && (
        <>
          {/* KPI Summary */}
          <div className="grid grid-cols-4 gap-4 px-4 py-4 border-b border-edge">
            <MiniKpi
              label="Total Payroll"
              value={money(data.summary.totalPayroll)}
              tone="revenue"
            />
            <MiniKpi label="Total Hours" value={hours(data.summary.totalHours)} tone="neutral" />
            <MiniKpi
              label="Contractors"
              value={String(data.summary.contractorCount)}
              tone="neutral"
            />
            <MiniKpi
              label="vs. Prior Period"
              value={
                data.summary.deltaPct !== null
                  ? `${Number(data.summary.deltaPct) > 0 ? '+' : ''}${data.summary.deltaPct}%`
                  : 'N/A'
              }
              tone={
                data.summary.deltaPct !== null && Math.abs(Number(data.summary.deltaPct)) > 15
                  ? 'caution'
                  : 'neutral'
              }
            />
          </div>

          {/* Active contractors */}
          <Table
            head={[
              { label: 'Contractor' },
              { label: 'Customer' },
              { label: 'Type' },
              { label: 'Total Hours', align: 'right' },
              { label: 'Pay Rate', align: 'right' },
              { label: 'Gross Pay', align: 'right' },
              { label: 'Delta', align: 'right' },
              { label: '' },
            ]}
          >
            {active.map((w) => (
              <PayrollWorkerRow
                key={w.workerId}
                worker={w}
                expanded={expandedId === w.workerId}
                onToggle={() => setExpandedId(expandedId === w.workerId ? null : w.workerId)}
                onHold={() => holdWorker(w.workerId)}
                deltaTone={deltaTone}
              />
            ))}
          </Table>

          {/* Held contractors */}
          {held.length > 0 && (
            <div className="border-t-2 border-caution/30">
              <div className="px-4 py-2 bg-caution/5">
                <p className="text-xs font-medium text-caution uppercase tracking-wide">
                  Held ({held.length})
                </p>
              </div>
              <Table
                head={[
                  { label: 'Contractor' },
                  { label: 'Customer' },
                  { label: 'Type' },
                  { label: 'Total Hours', align: 'right' },
                  { label: 'Pay Rate', align: 'right' },
                  { label: 'Gross Pay', align: 'right' },
                  { label: 'Delta', align: 'right' },
                  { label: '' },
                ]}
              >
                {held.map((w) => (
                  <tr
                    key={w.workerId}
                    className="border-b border-edge/50 last:border-0 bg-caution/5"
                  >
                    <Cell>{w.workerName}</Cell>
                    <Cell>{w.clientName}</Cell>
                    <Cell muted>{w.workerType === 'contractor_1099' ? '1099' : 'W2'}</Cell>
                    <Cell align="right" mono>
                      {hours(w.totalHours)}
                    </Cell>
                    <Cell align="right" mono>
                      {rate(w.payRate)}
                    </Cell>
                    <Cell align="right" mono>
                      <span className="text-revenue">{money(w.totalPay)}</span>
                    </Cell>
                    <Cell align="right" mono>
                      {w.deltaPct !== null && (
                        <span className={deltaTone(w.deltaPct)}>
                          {Number(w.deltaPct) > 0 ? '+' : ''}
                          {w.deltaPct}%
                        </span>
                      )}
                    </Cell>
                    <Cell>
                      <button
                        type="button"
                        onClick={() => releaseWorker(w.workerId)}
                        className="rounded bg-profit/10 px-2 py-1 text-[10px] font-medium text-profit hover:bg-profit/20 transition-colors"
                      >
                        Release
                      </button>
                    </Cell>
                  </tr>
                ))}
              </Table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function GpTabContent({ periodId }: { periodId?: string }) {
  const [search, setSearch] = useState('');
  const [showFormulas, setShowFormulas] = useState(false);
  const { data, error, loading } = useAsyncData<GpStageData>(
    () => api.stageGp(periodId),
    [periodId],
    'Could not load GP data.',
  );

  const accessor = useCallback((row: GpStageRow, key: string): unknown => {
    const map: Record<string, unknown> = {
      name: row.name,
      client: row.clientName,
      payRate: row.payRate,
      load: row.loadFactor,
      loadedCost: row.loadedCostPerHour,
      billRate: row.billRate,
      gpPerHour: row.grossProfitPerHour,
      hours: row.hours,
      expenses: row.expenses,
      subVendor: row.subVendorCost,
      gp: row.grossProfit,
      billed: row.billed,
    };
    return map[key];
  }, []);

  const filtered =
    data !== null
      ? data.rows.filter(
          (r) =>
            search === '' ||
            r.name.toLowerCase().includes(search.toLowerCase()) ||
            r.clientName.toLowerCase().includes(search.toLowerCase()),
        )
      : [];

  const { sorted, sortKey, sortDirection, onSort } = useSortedRows(filtered, accessor);

  if (error !== null) return <PanelError message={error} />;
  if (loading || data === null) return <PanelLoading label="Loading GP calculations…" />;

  const avgGp =
    data.rows.length > 0
      ? data.rows.reduce((sum, r) => sum + Number(r.grossProfit), 0) / data.rows.length
      : 0;

  return (
    <div>
      <div className="border-b border-edge bg-surface-overlay/50 px-4 py-2.5">
        <code className="text-xs text-ink-muted font-mono">
          GP/hr = Bill Rate − (Pay Rate × Load Factor) &nbsp;|&nbsp; Weekly GP = GP/hr × Hours −
          Expenses − Sub Vendor &nbsp;|&nbsp; Billed = Bill Rate × Hours
        </code>
      </div>
      <div className="border-b border-edge px-4 py-3">
        <div className="flex items-center justify-end gap-3">
          <ShowFormulasToggle enabled={showFormulas} onToggle={setShowFormulas} />
          <div className="w-48">
            <SearchBar value={search} onChange={setSearch} placeholder="Filter…" />
          </div>
          <ExportButton
            data={sorted.map((r) => ({
              contractor: r.name,
              customer: r.clientName,
              type: r.workerType,
              payRate: rate(r.payRate),
              loadFactor: r.loadFactor,
              loadedCost: rate(r.loadedCostPerHour),
              billRate: rate(r.billRate),
              gpPerHour: rate(r.grossProfitPerHour),
              hours: hours(r.hours),
              expenses: money(r.expenses),
              subVendor: money(r.subVendorCost),
              weeklyGp: money(r.grossProfit),
              billed: money(r.billed),
            }))}
            filename="gp-calculation"
            headers={[
              { key: 'contractor', label: 'Contractor' },
              { key: 'customer', label: 'Customer' },
              { key: 'type', label: 'Type' },
              { key: 'payRate', label: 'Pay Rate' },
              { key: 'loadFactor', label: 'Load Factor' },
              { key: 'loadedCost', label: 'Loaded Cost' },
              { key: 'billRate', label: 'Bill Rate' },
              { key: 'gpPerHour', label: 'GP/hr' },
              { key: 'hours', label: 'Hours' },
              { key: 'expenses', label: 'Expenses' },
              { key: 'subVendor', label: 'Sub Vendor' },
              { key: 'weeklyGp', label: 'Weekly GP' },
              { key: 'billed', label: 'Billed' },
            ]}
          />
        </div>
      </div>
      <div className="flex items-center gap-5 px-4 py-2 border-b border-edge text-[10px]">
        <span className="font-medium text-ink-faint uppercase tracking-wide">Legend</span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-sm bg-profit/20 border border-profit/30" />
          <span className="text-ink-muted">Normal</span>
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-sm bg-blue-100 border border-blue-300" />
          <span className="text-ink-muted">Above 2x average GP</span>
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-sm bg-caution/20 border border-caution/30" />
          <span className="text-ink-muted">Negative GP</span>
        </span>
      </div>
      <Table
        head={[
          { label: 'Contractor', sortKey: 'name' },
          { label: 'Customer', sortKey: 'client' },
          { label: 'Pay Rate', align: 'right', sortKey: 'payRate' },
          { label: 'Load', align: 'right', sortKey: 'load' },
          { label: 'Loaded Cost', align: 'right', sortKey: 'loadedCost' },
          { label: 'Bill Rate', align: 'right', sortKey: 'billRate' },
          { label: 'GP/hr', align: 'right', sortKey: 'gpPerHour' },
          { label: 'Hours', align: 'right', sortKey: 'hours' },
          { label: 'Expenses', align: 'right', sortKey: 'expenses' },
          { label: 'Sub Vendor', align: 'right', sortKey: 'subVendor' },
          { label: 'Weekly GP', align: 'right', sortKey: 'gp' },
          { label: 'Billed', align: 'right', sortKey: 'billed' },
        ]}
        sortKey={sortKey}
        sortDirection={sortDirection}
        onSort={onSort}
      >
        {sorted.map((r) => {
          const negative = r.grossProfit.startsWith('-');
          const gpNum = Number(r.grossProfit);
          const highGp = avgGp > 0 && gpNum > avgGp * 2;
          return (
            <tr
              key={r.id}
              className={`border-b border-edge/50 last:border-0 ${negative ? 'bg-caution/5' : highGp ? 'bg-blue-50' : ''}`}
            >
              <Cell>{r.name}</Cell>
              <Cell>{r.clientName}</Cell>
              <Cell align="right" mono>
                {rate(r.payRate)}
              </Cell>
              <Cell align="right" mono muted>
                {Number(r.loadFactor).toFixed(3)}
              </Cell>
              <Cell align="right" mono>
                {showFormulas ? (
                  <FormulaCell
                    formula={`${rate(r.payRate)} × ${Number(r.loadFactor).toFixed(3)}`}
                  />
                ) : (
                  <FormulaTooltip
                    formula="Pay Rate × Load Factor"
                    variables={[
                      { label: 'Pay Rate', value: rate(r.payRate) },
                      { label: 'Load Factor', value: Number(r.loadFactor).toFixed(3) },
                    ]}
                    result={rate(r.loadedCostPerHour)}
                  >
                    {rate(r.loadedCostPerHour)}
                  </FormulaTooltip>
                )}
              </Cell>
              <Cell align="right" mono>
                {rate(r.billRate)}
              </Cell>
              <Cell align="right" mono>
                {showFormulas ? (
                  <FormulaCell
                    formula={`${rate(r.billRate)} − ${rate(r.loadedCostPerHour)}`}
                    negative={negative}
                  />
                ) : (
                  <FormulaTooltip
                    formula="Bill Rate − Loaded Cost"
                    variables={[
                      { label: 'Bill Rate', value: rate(r.billRate) },
                      { label: 'Loaded Cost', value: rate(r.loadedCostPerHour) },
                    ]}
                    result={rate(r.grossProfitPerHour)}
                  >
                    <span className={negative ? 'text-caution' : 'text-profit'}>
                      {rate(r.grossProfitPerHour)}
                    </span>
                  </FormulaTooltip>
                )}
              </Cell>
              <Cell align="right" mono>
                {hours(r.hours)}
              </Cell>
              <Cell align="right" mono>
                {money(r.expenses)}
              </Cell>
              <Cell align="right" mono>
                {money(r.subVendorCost)}
              </Cell>
              <Cell align="right" mono>
                {showFormulas ? (
                  <FormulaCell
                    formula={`${rate(r.grossProfitPerHour)} × ${hours(r.hours)} − ${money(r.expenses)} − ${money(r.subVendorCost)}`}
                    negative={negative}
                  />
                ) : (
                  <FormulaTooltip
                    formula="GP/hr × Hours − Expenses − Sub Vendor"
                    variables={[
                      { label: 'GP/hr', value: rate(r.grossProfitPerHour) },
                      { label: 'Hours', value: hours(r.hours) },
                      { label: 'Expenses', value: money(r.expenses) },
                      { label: 'Sub Vendor', value: money(r.subVendorCost) },
                    ]}
                    result={money(r.grossProfit)}
                  >
                    <span
                      className={
                        negative
                          ? 'text-caution font-medium'
                          : highGp
                            ? 'text-blue-600 font-medium'
                            : 'text-profit font-medium'
                      }
                    >
                      {money(r.grossProfit)}
                    </span>
                  </FormulaTooltip>
                )}
              </Cell>
              <Cell align="right" mono>
                {showFormulas ? (
                  <FormulaCell formula={`${rate(r.billRate)} × ${hours(r.hours)}`} />
                ) : (
                  <FormulaTooltip
                    formula="Bill Rate × Hours"
                    variables={[
                      { label: 'Bill Rate', value: rate(r.billRate) },
                      { label: 'Hours', value: hours(r.hours) },
                    ]}
                    result={money(r.billed)}
                  >
                    <span className="text-revenue">{money(r.billed)}</span>
                  </FormulaTooltip>
                )}
              </Cell>
            </tr>
          );
        })}
        <tr className="border-t-2 border-edge bg-surface-overlay/30 font-medium">
          <Cell>
            <span className="font-semibold text-ink">Totals</span>
          </Cell>
          <Cell>{''}</Cell>
          <Cell align="right">{''}</Cell>
          <Cell align="right">{''}</Cell>
          <Cell align="right">{''}</Cell>
          <Cell align="right">{''}</Cell>
          <Cell align="right">{''}</Cell>
          <Cell align="right" mono>
            {hours(data.totals.hours)}
          </Cell>
          <Cell align="right" mono>
            {money(data.totals.expenses)}
          </Cell>
          <Cell align="right" mono>
            {money(data.totals.subVendorCost)}
          </Cell>
          <Cell align="right" mono>
            <span className="text-profit font-semibold">{money(data.totals.grossProfit)}</span>
          </Cell>
          <Cell align="right" mono>
            <span className="text-revenue font-semibold">{money(data.totals.billed)}</span>
          </Cell>
        </tr>
      </Table>
    </div>
  );
}

function CommissionTabContent({ periodId }: { periodId?: string }) {
  const { data, error, loading } = useAsyncData<CommissionStageData>(
    () => api.stageCommission(periodId),
    [periodId],
    'Could not load commission data.',
  );

  if (error !== null) return <PanelError message={error} />;
  if (loading || data === null) return <PanelLoading label="Loading commissions…" />;

  return (
    <div>
      <div className="grid grid-cols-3 gap-4 px-4 py-4 border-b border-edge">
        <MiniKpi label="Total GP credited" value={money(data.totals.totalGp)} tone="profit" />
        <MiniKpi
          label="Total commission"
          value={money(data.totals.totalCommission)}
          tone="revenue"
        />
        <MiniKpi label="Recruiters" value={String(data.recruiters.length)} tone="neutral" />
      </div>
      {data.tiers.length > 0 && <TierLadder tiers={data.tiers} />}
      <div className="divide-y divide-edge">
        {data.recruiters.map((recruiter) => (
          <RecruiterCard key={recruiter.id} recruiter={recruiter} />
        ))}
      </div>
    </div>
  );
}

function PayrollWorkerRow({
  worker,
  expanded,
  onToggle,
  onHold,
  deltaTone,
}: {
  worker: PayrollPreview['lines'][number];
  expanded: boolean;
  onToggle: () => void;
  onHold: () => void;
  deltaTone: (pct: string | null) => string;
}) {
  const w = worker;
  return (
    <>
      <tr
        className="border-b border-edge/50 last:border-0 cursor-pointer transition-colors hover:bg-surface-overlay/60"
        onClick={onToggle}
      >
        <Cell>
          <div className="flex items-center gap-2">
            <span>{w.workerName}</span>
            {w.warnings.map((warn) => (
              <span
                key={warn}
                className={`rounded-full px-1.5 py-0.5 text-[9px] font-medium uppercase ${
                  warn === 'OT'
                    ? 'bg-caution/10 text-caution'
                    : warn === 'New'
                      ? 'bg-revenue/10 text-revenue'
                      : 'bg-red-50 text-red-600'
                }`}
              >
                {warn}
              </span>
            ))}
          </div>
        </Cell>
        <Cell>{w.clientName}</Cell>
        <Cell muted>{w.workerType === 'contractor_1099' ? '1099' : 'W2'}</Cell>
        <Cell align="right" mono>
          {hours(w.totalHours)}
        </Cell>
        <Cell align="right" mono>
          {rate(w.payRate)}
        </Cell>
        <Cell align="right" mono>
          <FormulaTooltip
            formula="Hours × Pay Rate"
            variables={[
              { label: 'Hours', value: hours(w.totalHours) },
              { label: 'Pay Rate', value: rate(w.payRate) },
            ]}
            result={money(w.totalPay)}
          >
            <span className="text-revenue">{money(w.totalPay)}</span>
          </FormulaTooltip>
        </Cell>
        <Cell align="right" mono>
          {w.deltaPct !== null ? (
            <span className={deltaTone(w.deltaPct)}>
              {Number(w.deltaPct) > 0 ? '+' : ''}
              {w.deltaPct}%
            </span>
          ) : (
            <span className="text-ink-faint">—</span>
          )}
        </Cell>
        <Cell>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onHold();
            }}
            className="rounded bg-caution/10 px-2 py-1 text-[10px] font-medium text-caution hover:bg-caution/20 transition-colors"
          >
            Hold
          </button>
        </Cell>
      </tr>
      {expanded && w.earningBreakdown.length > 0 && (
        <tr>
          <td colSpan={8} className="px-8 py-3 bg-surface-overlay/30 border-b border-edge/50">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-ink-faint">
                  <th className="text-left py-1 font-medium">Earning Type</th>
                  <th className="text-right py-1 font-medium">Hours</th>
                  <th className="text-right py-1 font-medium">Pay</th>
                </tr>
              </thead>
              <tbody>
                {w.earningBreakdown.map((e, idx) => (
                  <tr key={idx} className="text-ink-muted">
                    <td className="py-1">{e.label}</td>
                    <td className="py-1 text-right font-mono">{hours(e.hours)}</td>
                    <td className="py-1 text-right font-mono">{money(e.pay)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </td>
        </tr>
      )}
    </>
  );
}

/* -------------------------------------------------------------------------- */
/*  Stage 6: Approve                                                           */
/* -------------------------------------------------------------------------- */

function ApprovePanel({ onRefresh, periodId }: { onRefresh?: () => void; periodId?: string }) {
  const [approving, setApproving] = useState(false);
  const [approveError, setApproveError] = useState<string | null>(null);

  const { data, error, loading, reload } = useAsyncData<ApproveStageData>(
    () => api.stageApprove(periodId),
    [periodId],
    'Could not load approval data.',
  );

  if (error !== null) return <PanelError message={error} />;
  if (loading || data === null) return <PanelLoading label="Loading approval data…" />;

  const canApprove = data.periodStatus === 'in_review';

  const priorDelta =
    data.prior !== null
      ? (() => {
          const current = Number(data.current.totalGrossPay);
          const prior = Number(data.prior.totalGrossPay);
          if (prior === 0) return null;
          const pct = ((current - prior) / prior) * 100;
          return { amount: data.prior.totalGrossPay, pct };
        })()
      : null;

  const statusTone =
    data.periodStatus === 'approved' ||
    data.periodStatus === 'submitted' ||
    data.periodStatus === 'closed'
      ? 'profit'
      : 'caution';

  return (
    <div className="rounded-lg border border-edge bg-surface-base">
      <div className="border-b border-edge px-4 py-3">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold text-ink">Approve Payroll Batch</p>
            <p className="text-xs text-ink-muted mt-0.5">{data.period.periodLabel}</p>
          </div>
          <span
            className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${statusTone === 'profit' ? 'bg-profit/10 text-profit' : 'bg-caution/10 text-caution'}`}
          >
            {data.periodStatus.charAt(0).toUpperCase() + data.periodStatus.slice(1)}
          </span>
        </div>
      </div>
      <div className="px-4 py-4 space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="rounded-lg border border-edge px-3 py-2.5">
            <p className="text-[10px] font-medium uppercase tracking-wide text-ink-faint">
              Total Gross Pay
            </p>
            <p className="mt-1 text-lg font-semibold text-ink">
              {money(data.current.totalGrossPay)}
            </p>
            <p className="text-xs text-ink-muted mt-0.5">
              {hours(data.current.totalHours)} hours · {data.current.contractorCount} contractors
            </p>
          </div>
          <div className="rounded-lg border border-edge px-3 py-2.5">
            <p className="text-[10px] font-medium uppercase tracking-wide text-ink-faint">
              vs. Prior Period
            </p>
            {priorDelta !== null ? (
              <>
                <p className="mt-1 text-lg font-semibold text-ink">
                  {money(priorDelta.amount)}{' '}
                  <span
                    className={`text-sm font-normal ${priorDelta.pct > 0 ? 'text-caution' : 'text-profit'}`}
                  >
                    {priorDelta.pct > 0 ? '+' : ''}
                    {priorDelta.pct.toFixed(1)}%
                  </span>
                </p>
              </>
            ) : (
              <p className="mt-1 text-lg font-semibold text-ink-muted">No prior period</p>
            )}
          </div>
        </div>

        <div className="rounded-lg border border-edge bg-surface-overlay/30 px-3 py-3">
          <p className="text-xs font-medium text-ink mb-2">Confirmation Checklist</p>
          <div className="grid grid-cols-2 gap-2">
            <ChecklistItem label="Hours collected" checked={data.checklist.hoursCollected} />
            <ChecklistItem label="Hours verified" checked={data.checklist.hoursVerified} />
            <ChecklistItem label="GP calculated" checked={data.checklist.gpCalculated} />
            <ChecklistItem
              label="Commissions calculated"
              checked={data.checklist.commissionCalculated}
            />
          </div>
        </div>

        {approveError && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
            {approveError}
          </div>
        )}
        <div className="flex justify-end">
          {canApprove ? (
            <button
              type="button"
              disabled={approving}
              onClick={async () => {
                setApproving(true);
                setApproveError(null);
                try {
                  await api.approveBatch(periodId);
                  reload();
                  onRefresh?.();
                } catch (e) {
                  setApproveError(e instanceof Error ? e.message : 'Approval failed');
                } finally {
                  setApproving(false);
                }
              }}
              className={`inline-flex items-center gap-1.5 rounded-lg border px-4 py-2 text-sm font-medium transition-colors ${
                approving
                  ? 'border-edge bg-surface-overlay text-ink-faint cursor-wait'
                  : 'border-profit bg-profit/10 text-profit hover:bg-profit/20'
              }`}
            >
              {approving ? 'Approving…' : 'Approve Batch'}
            </button>
          ) : (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-profit/10 px-3 py-1.5 text-xs font-medium text-profit">
              <CheckIcon /> Approved
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function ChecklistItem({ label, checked }: { label: string; checked: boolean }) {
  return (
    <div className="flex items-center gap-2 text-xs">
      {checked ? (
        <span className="flex h-4 w-4 items-center justify-center rounded-full bg-profit/15 text-profit">
          <CheckIcon />
        </span>
      ) : (
        <span className="flex h-4 w-4 items-center justify-center rounded-full bg-surface-overlay border border-edge">
          <span className="h-1.5 w-1.5 rounded-full bg-ink-faint" />
        </span>
      )}
      <span className={checked ? 'text-ink' : 'text-ink-muted'}>{label}</span>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Stage 7: Submit to ADP                                                     */
/* -------------------------------------------------------------------------- */

function SubmitPanel({ onRefresh, periodId }: { onRefresh?: () => void; periodId?: string }) {
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { data, error, loading, reload } = useAsyncData<SubmitStageData>(
    () => api.stageSubmit(periodId),
    [periodId],
    'Could not load submission data.',
  );

  if (error !== null) return <PanelError message={error} />;
  if (loading || data === null) return <PanelLoading label="Loading submission data…" />;

  const canSubmit = data.periodStatus === 'approved' && data.submissionStatus === 'pending';
  const isSubmitted =
    data.submissionStatus === 'submitted' || data.submissionStatus === 'confirmed';
  const displayProgress = isSubmitted ? 100 : 0;

  return (
    <div className="rounded-lg border border-edge bg-surface-base">
      <div className="border-b border-edge px-4 py-3">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold text-ink">Submit to ADP</p>
            <p className="text-xs text-ink-muted mt-0.5">{data.period.periodLabel}</p>
          </div>
          <span className="inline-flex items-center rounded-full bg-profit/10 px-2 py-0.5 text-xs font-medium text-profit">
            Dry-run mode
          </span>
        </div>
      </div>
      <div className="px-4 py-4 space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="rounded-lg border border-edge px-3 py-2.5">
            <p className="text-[10px] font-medium uppercase tracking-wide text-ink-faint">
              Pay Records
            </p>
            <p className="mt-1 text-lg font-semibold text-ink">
              {data.contractorCount}{' '}
              <span className="text-sm font-normal text-ink-muted">
                {isSubmitted ? 'submitted' : 'ready for submission'}
              </span>
            </p>
          </div>
          <div className="rounded-lg border border-edge px-3 py-2.5">
            <p className="text-[10px] font-medium uppercase tracking-wide text-ink-faint">
              Idempotency Key
            </p>
            <p className="mt-1 text-sm font-mono text-ink-muted break-all">{data.idempotencyKey}</p>
          </div>
        </div>

        <div className="rounded-lg border border-edge px-3 py-2.5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-ink-muted">Submission progress</span>
            <span className="text-xs text-ink-faint">
              {isSubmitted
                ? data.contractorCount
                : Math.round((displayProgress / 100) * data.contractorCount)}{' '}
              of {data.contractorCount}
            </span>
          </div>
          <div className="h-2 rounded-full bg-surface-overlay overflow-hidden">
            <div
              className="h-full rounded-full bg-revenue transition-all duration-300"
              style={{ width: `${displayProgress}%` }}
            />
          </div>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-xs text-ink-faint">
            No records will be submitted to ADP in dry-run mode
          </span>
        </div>

        <div className="rounded-lg border border-edge bg-surface-overlay/30 px-3 py-2.5">
          <p className="text-xs text-ink-muted">
            ADP integration submits pay records via the Workers V2 API. Each submission uses a
            unique idempotency key to prevent duplicate payments. Earning codes are validated
            against the company's ADP <code className="text-ink">/meta</code> response before
            submission.
          </p>
        </div>

        {submitError && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
            {submitError}
          </div>
        )}
        <div className="flex justify-end">
          {canSubmit && !isSubmitted ? (
            <button
              type="button"
              disabled={submitting}
              onClick={async () => {
                setSubmitting(true);
                setSubmitError(null);
                try {
                  await api.submitBatch(periodId);
                  reload();
                  onRefresh?.();
                } catch (e) {
                  setSubmitError(e instanceof Error ? e.message : 'Submission failed');
                } finally {
                  setSubmitting(false);
                }
              }}
              className={`inline-flex items-center gap-1.5 rounded-lg border px-4 py-2 text-sm font-medium transition-colors ${
                submitting
                  ? 'border-edge bg-surface-overlay text-ink-faint cursor-wait'
                  : 'border-revenue bg-revenue/10 text-revenue hover:bg-revenue/20'
              }`}
            >
              {submitting ? 'Submitting…' : 'Submit (Dry Run)'}
            </button>
          ) : isSubmitted ? (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-profit/10 px-3 py-1.5 text-xs font-medium text-profit">
              <CheckIcon /> Submitted
            </span>
          ) : (
            <span className="text-xs text-ink-faint">Run must be approved before submission</span>
          )}
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Stage 8: Reconcile                                                         */
/* -------------------------------------------------------------------------- */

function ReconcilePanel({ onRefresh, periodId }: { onRefresh?: () => void; periodId?: string }) {
  const [closing, setClosing] = useState(false);
  const [closeError, setCloseError] = useState<string | null>(null);

  const { data, error, loading, reload } = useAsyncData<ReconcileStageData>(
    () => api.stageReconcile(periodId),
    [periodId],
    'Could not load reconciliation data.',
  );

  if (error !== null) return <PanelError message={error} />;
  if (loading || data === null) return <PanelLoading label="Loading reconciliation data…" />;

  const hasResults = data.results.length > 0;
  const canRunReconcile = data.periodStatus === 'submitted' && !hasResults;
  const allAcknowledged = hasResults && data.results.every((r) => r.status !== 'discrepancy');
  const canClose = hasResults && allAcknowledged && data.periodStatus === 'submitted';

  const matchRateNum = Number(data.matchRate.replace('%', ''));
  const matchTone =
    matchRateNum >= 100 ? 'text-profit' : matchRateNum >= 95 ? 'text-caution' : 'text-caution';

  async function handleAcknowledge(resultId: string) {
    await api.acknowledgeDiscrepancies([resultId]);
    reload();
  }

  async function handleClose() {
    setClosing(true);
    setCloseError(null);
    try {
      await api.closePeriod(periodId);
      reload();
      onRefresh?.();
    } catch (e) {
      setCloseError(e instanceof Error ? e.message : 'Close failed');
    } finally {
      setClosing(false);
    }
  }

  return (
    <div className="rounded-lg border border-edge bg-surface-base">
      <div className="border-b border-edge px-4 py-3">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold text-ink">Reconciliation</p>
            <p className="text-xs text-ink-muted mt-0.5">{data.period.periodLabel}</p>
          </div>
          <span
            className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${data.reconciled ? 'bg-profit/10 text-profit' : 'bg-caution/10 text-caution'}`}
          >
            {data.reconciled
              ? 'Reconciled'
              : data.periodStatus.charAt(0).toUpperCase() + data.periodStatus.slice(1)}
          </span>
        </div>
      </div>
      <div className="px-4 py-4 space-y-4">
        {/* KPIs */}
        <div className="grid grid-cols-3 gap-4">
          <div className="rounded-lg border border-edge px-3 py-2.5 text-center">
            <p className="text-2xl font-semibold text-profit">{data.matched}</p>
            <p className="text-[10px] font-medium uppercase tracking-wide text-ink-faint mt-1">
              Records match
            </p>
          </div>
          <div className="rounded-lg border border-edge px-3 py-2.5 text-center">
            <p
              className={`text-2xl font-semibold ${data.discrepancies > 0 ? 'text-caution' : 'text-ink'}`}
            >
              {data.discrepancies}
            </p>
            <p className="text-[10px] font-medium uppercase tracking-wide text-ink-faint mt-1">
              Discrepancies
            </p>
          </div>
          <div className="rounded-lg border border-edge px-3 py-2.5 text-center">
            <p className={`text-2xl font-semibold ${matchTone}`}>{data.matchRate}</p>
            <p className="text-[10px] font-medium uppercase tracking-wide text-ink-faint mt-1">
              Match rate
            </p>
          </div>
        </div>

        {/* Comparison Table */}
        {hasResults && (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-edge text-ink-faint">
                  <th className="text-left py-2 px-2 font-medium">Contractor</th>
                  <th className="text-left py-2 px-2 font-medium">Customer</th>
                  <th className="text-right py-2 px-2 font-medium">Source Hours</th>
                  <th className="text-right py-2 px-2 font-medium">ADP Hours</th>
                  <th className="text-right py-2 px-2 font-medium">Source Pay</th>
                  <th className="text-right py-2 px-2 font-medium">ADP Pay</th>
                  <th className="text-center py-2 px-2 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {data.results.map((r) => {
                  const isDiscrep = r.status === 'discrepancy' || r.status === 'acknowledged';
                  return (
                    <tr
                      key={r.id}
                      className={`border-b border-edge/50 last:border-0 ${
                        r.status === 'discrepancy'
                          ? 'bg-caution/5'
                          : r.status === 'acknowledged'
                            ? 'bg-amber-50'
                            : ''
                      }`}
                    >
                      <td className="py-2 px-2 text-ink">{r.workerName}</td>
                      <td className="py-2 px-2 text-ink-muted">{r.clientName}</td>
                      <td className="py-2 px-2 text-right font-mono text-ink">
                        {hours(r.sourceHours)}
                      </td>
                      <td
                        className={`py-2 px-2 text-right font-mono ${isDiscrep ? 'text-caution font-medium' : 'text-ink'}`}
                      >
                        {r.adpHours !== null ? hours(r.adpHours) : '—'}
                      </td>
                      <td className="py-2 px-2 text-right font-mono text-ink">
                        {money(r.sourcePay)}
                      </td>
                      <td
                        className={`py-2 px-2 text-right font-mono ${isDiscrep ? 'text-caution font-medium' : 'text-ink'}`}
                      >
                        {r.adpPay !== null ? money(r.adpPay) : '—'}
                      </td>
                      <td className="py-2 px-2 text-center">
                        {r.status === 'matched' ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-profit/10 px-2 py-0.5 text-profit">
                            <CheckIcon /> Match
                          </span>
                        ) : r.status === 'acknowledged' ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-amber-700">
                            Acknowledged
                          </span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => handleAcknowledge(r.id)}
                            className="rounded bg-caution/10 px-2 py-1 text-[10px] font-medium text-caution hover:bg-caution/20 transition-colors"
                          >
                            Acknowledge
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Explainer (only before reconciliation) */}
        {!hasResults && (
          <div className="rounded-lg border border-edge bg-surface-overlay/30 px-3 py-2.5">
            <p className="text-xs text-ink-muted">
              Reconciliation compares the Oorwin source hours against what ADP recorded after
              submission. Any mismatch in hours, pay rate, or earning code is flagged for manual
              review before the period can be closed.
            </p>
          </div>
        )}

        {closeError !== null && <p className="text-xs text-caution">{closeError}</p>}

        {/* Actions */}
        <div className="flex justify-end gap-2">
          {canRunReconcile && (
            <button
              type="button"
              onClick={() => reload()}
              className="inline-flex items-center gap-1.5 rounded-lg border border-profit bg-profit/10 px-3 py-1.5 text-xs font-medium text-profit hover:bg-profit/20 transition-colors"
            >
              Load Reconciliation Results
            </button>
          )}
          {canClose && (
            <button
              type="button"
              disabled={closing}
              onClick={handleClose}
              className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors ${
                closing
                  ? 'opacity-50 cursor-wait border-emerald-400 bg-emerald-600 text-white'
                  : 'border-emerald-400 bg-emerald-600 text-white hover:bg-emerald-700'
              }`}
            >
              {closing ? 'Closing…' : 'Close Period'}
            </button>
          )}
          {data.reconciled && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-profit/10 px-3 py-1.5 text-xs font-medium text-profit">
              <CheckIcon /> Period Closed
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Shared helpers                                                             */
/* -------------------------------------------------------------------------- */

function PanelError({ message }: { message: string }) {
  return (
    <div className="rounded-lg border border-edge bg-surface-base px-4 py-6">
      <p className="text-sm text-caution">{message}</p>
    </div>
  );
}

function PanelLoading({ label }: { label: string }) {
  return (
    <div className="rounded-lg border border-edge bg-surface-base">
      <Spinner label={label} />
    </div>
  );
}

function MiniKpi({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone: 'profit' | 'revenue' | 'neutral' | 'caution';
}) {
  const color =
    tone === 'profit'
      ? 'text-profit'
      : tone === 'revenue'
        ? 'text-revenue'
        : tone === 'caution'
          ? 'text-caution'
          : 'text-ink';
  return (
    <div>
      <p className="text-[10px] font-medium uppercase tracking-wide text-ink-faint">{label}</p>
      <p className={`mt-1 text-lg font-semibold ${color}`}>{value}</p>
    </div>
  );
}

function CheckIcon() {
  return (
    <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  );
}

function FlagIcon() {
  return (
    <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2z"
      />
    </svg>
  );
}

function PlayIcon() {
  return (
    <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
      />
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}
